import { FormattedMessage } from "react-intl";
import { graphql, useFragment } from "react-relay";
import { generatePath, Link } from "react-router";
import {
  Certificate01Icon,
  Code02Icon,
  Edit03Icon,
  Flag01Icon,
  SlashCircle01Icon,
} from "@/icons";
import { Badge } from "@/kit/ui/badge";
import { cn } from "@/utils/tailwind";
import EntityProfilePic from "./EntityProfilePic";
import { RelativeTime } from "./RelativeTime";
import { WorkspaceCardFragment$key } from "./__generated__/WorkspaceCardFragment.graphql";
import { VDivided, VDividedItem } from "./Divided";
import VoteDisplay from "./VoteDisplay";
import { splitWorkspaceSlug } from "@/utils/routing";

const Fragment = graphql`
  fragment WorkspaceCardFragment on Workspace {
    id
    name
    slug
    createdAt
    license
    owner {
      username
      ...EntityProfilePicFragment @arguments(thumbnail: true)
    }
    tags(first: 10) {
      nodes {
        id
        name
      }
    }
    fromCompetition @ifAllowed {
      id
      title
    }
    ...VoteDisplayFragment
  }
`;

interface WorkspaceCardProps {
  workspace: WorkspaceCardFragment$key;
  asElement?:
    | keyof JSX.IntrinsicElements
    | React.ComponentType<React.PropsWithChildren>
    | undefined;
}

function WorkspaceCard(props: WorkspaceCardProps) {
  const workspace = useFragment(Fragment, props.workspace);
  const Comp = props.asElement ?? "div";
  return (
    <Comp className="group">
      <div className="group w-full rounded-xl border border-grey-200 bg-grey-25 transition-shadow shadow-sm group-hover:shadow-lg group-focus:outline-solid group-focus:outline-1 group-focus:outline-offset-1 group-focus:outline-brand-100  ">
        <Link
          to={generatePath("/:owner/:slug", splitWorkspaceSlug(workspace.slug))}
          className="w-full p-5 rounded-xl flex flex-col gap-4 bg-white shadow-[0_1px_3px_0_rgba(16,24,40,0.10),0_1px_2px_0_rgba(16,24,40,0.06)]"
        >
          <div className="flex flex-row justify-start items-start gap-3">
            <div className="bg-brand-50 flex items-center justify-center rounded-lg w-8 h-8">
              <Code02Icon className="text-green-600 w-5 h-5" />
            </div>
            <p className="flex-1 justify-start text-grey-900 text-lg font-semibold leading-7 transition-colors group-hover:text-brand-500 line-clamp-2 max-h-14 min-h-14 overflow-hidden">
              {workspace.owner.username} / {workspace.name}
            </p>
          </div>

          <div className="inline-flex justify-start items-end content-end truncate">
            <div className="text-grey-500 text-end truncate">
              {workspace.fromCompetition && (
                <Badge variant="secondary" hasPrefix className="gap-1 me-1">
                  <Flag01Icon className="w-3 h-3" />
                  {workspace.fromCompetition.title}
                </Badge>
              )}
              {workspace.tags.nodes.length ? (
                workspace.tags.nodes.map((node, index) => (
                  <span key={node.id}>
                    <Badge className={cn(index === 0 ? "me-1" : "mx-1")}>
                      {node.name}
                    </Badge>
                  </span>
                ))
              ) : (
                <span>
                  <Badge hasPrefix className="gap-1">
                    <SlashCircle01Icon className="w-3 h-3 text-grey-500" />
                    <p className="text-center justify-start text-grey-700 text-sm font-medium leading-tight">
                      <FormattedMessage defaultMessage="No tags yet" />
                    </p>
                  </Badge>
                </span>
              )}
            </div>
          </div>

          <VDivided>
            <VDividedItem
              icon={Edit03Icon}
              title={<FormattedMessage defaultMessage="Created" />}
            >
              <RelativeTime date={new Date(workspace.createdAt)} />
            </VDividedItem>

            {workspace.license && (
              <VDividedItem icon={Certificate01Icon}>
                {workspace.license}
              </VDividedItem>
            )}
          </VDivided>
        </Link>

        <div className="p-5 flex flex-row items-center justify-between">
          <Link
            to={generatePath("/:username", {
              username: workspace.owner.username,
            })}
            className="flex flex-row items-center gap-3"
          >
            <EntityProfilePic entity={workspace.owner} size="10" />
            {workspace.owner.username}
          </Link>

          <VoteDisplay subject={workspace} kind="compact" />
        </div>
      </div>
    </Comp>
  );
}

export type { WorkspaceCardProps };
export { WorkspaceCard };
